import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { debounceTime, filter, merge, tap } from 'rxjs';
import { NotificationService } from '@aston/foundation';
import { TranslateService } from '@ngx-translate/core';
import { Events } from '@ngrx/signals/events';
import { inject } from '@angular/core';

import { factorFeaturesEvents } from '../../../factor-features-module/stores/factor-features/events';
import { WebSocketService } from '../../../shared-module/services/websocket.service';
import { WsEventType } from '../../../shared-module/enums/ws-event-type.enum';
import { ReferentialStore } from '../../../shared-module/stores/referential';
import { ImportConfigurationStatus, ImportType } from '../../enums';
import { IImportConfiguration, IImportItem } from '../../models';

export function withImportDetailListeners<_>() {
	return signalStoreFeature(
		{
			state: type<{
				importItem: IImportItem,
			}>(),
			methods: type<{ 
				loadImport: () => Promise<void>,
				loadImportConfig: () => Promise<IImportConfiguration>,
				loadReportErrorColumns: () => Promise<void>,
				loadImportSynthesis: () => Promise<IImportItem>,
			 }>()
		},

		withMethods(store => {
			const e = inject(Events);
			const ws = inject(WebSocketService);
			const toasts = inject(NotificationService);
			const referential = inject(ReferentialStore);
			const translateService = inject(TranslateService);
			
			const listenReloadImportConfig = () => merge(
				ws.ofType(WsEventType.RefreshFactorFeatures, WsEventType.RefreshExtraDataDefinition),
				e.on(factorFeaturesEvents.creditLimitChanged),
			).pipe(tap(() => {
				store.loadImportConfig();
				referential.loadReferential();
			}));

			const listenAccountingDraftImportCancelledEvent = () => ws.ofType(WsEventType.AccountingDraftImportCancelled).pipe(
				filter(_ => store.importItem()?.type === ImportType.Accounting),
				tap(_ => {
					toasts.warning({ message: 'Import.Creation.Notifications.AccountingDraftImportCancelled', autoClose: true })
					store.loadImport();
				})
			);
			
			const listenReloadImportEvent = () => ws.ofType(WsEventType.ImportFindValuesToMap).pipe(
				tap(event => {
					const eventObject: any = {};

					try { Object.assign(eventObject, JSON.parse(event)) }
					catch { console.error('cannot parse import event:', event) }
					
					if (eventObject.Name?.match(/Malformed/)) {
						store.loadImport();
						patchState(store, {
							importItem: {
								...store.importItem(),
								importFileConfiguration: {
									...store.importItem().importFileConfiguration,
									importConfigurationStatus: ImportConfigurationStatus.None,
									extraMessage: translateService.instant('Errors.MalformedImportLine') + ' : ' + eventObject.LineNumbers?.slice(0, 10).join(', ')
								}
							}
						});

						return;
					}

					toasts.success({ message: 'Import.Creation.Notifications.HeadersMappingSuccess', autoClose: true })
					store.loadImport();
				}),
			);
			
			const listenReloadImportWithErrorEvent = () => ws.ofType(WsEventType.PreImport).pipe(
				tap(_ => {
					toasts.success({ message: 'Import.Creation.Notifications.CashingConsistencyCheckSuccess', autoClose: true })
					store.loadImport();
					store.loadReportErrorColumns();
				})
			);
			
			const listenCompleteImportEvent = () => ws.ofType(WsEventType.ImportCompletion).pipe(
				tap(_ => {
					if (store.importItem()?.id) {
						toasts.success({ message: 'Import.Creation.Notifications.CashingCompletionSuccess', autoClose: true })
						store.loadImportSynthesis();
					}
				}),
			);
		
			const listenGlobalCompleteImportEvent = () => ws.ofType(WsEventType.GlobalWorkflowFailure, WsEventType.GlobalWorkflowCompleted).pipe(
				tap(_ => { if (store.importItem()?.id) store.loadImportSynthesis(); }),
			);
			
			const listenGlobalWorkflowComplete = () => ws.ofType(WsEventType.GlobalWorkflowCompleted).pipe(
				debounceTime(500),
				tap(_ => toasts.success({ message: 'Import.Creation.Status.GlobalWorkflowCompleted', autoClose: true })),
			);

			const listenGlobalWorkflowFailure = () => ws.ofType(WsEventType.GlobalWorkflowFailure).pipe(
				debounceTime(500),
				tap(_ => toasts.error({ message: 'Import.Creation.Status.GlobalWorkflowFailure', autoClose: true })),
			);

			return {
				listenReloadImportConfig,
				listenAccountingDraftImportCancelledEvent,
				listenReloadImportEvent,
				listenReloadImportWithErrorEvent,
				listenCompleteImportEvent,
				listenGlobalCompleteImportEvent,
				listenGlobalWorkflowComplete,
				listenGlobalWorkflowFailure,
			}
		})
	)
}